import { useState, useEffect, useRef } from 'react';
import {
  Plus,
  Search,
  Download,
  Eye,
  Trash2,
  X,
  Calendar,
  Mail,
  AlertCircle,
  ExternalLink,
  FileText,
  User,
} from 'lucide-react';

const BASE_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookingInvoice';

const PAGINATED_BOOKINGS_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookings/viewAllBoookings';

const BOOKING_INVOICE_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/bookingInvoice';

const CUSTOMERS_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/customer';

const RESOURCE_URL =
  'https://faas-sgp1-18bc02ac.doserverless.co/api/v1/web/fn-eabd0529-e853-4bfe-85de-7e0fb90bd150/resource/viewAllResource';

const getAccessToken = () =>
  sessionStorage.getItem('access_token');

const apiRequest = async (
  endpoint,
  method = 'GET',
  data = null
) => {
  const url = `${BASE_URL}${endpoint}`;

  const options = {
    method,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${getAccessToken()}`,
    },
  };

  if (
    data &&
    (method === 'POST' || method === 'DELETE')
  ) {
    options.body = JSON.stringify(data);
  }

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      const errorData = await response
        .json()
        .catch(() => ({}));

      const errorMessage =
        errorData.error ||
        errorData.message ||
        `HTTP error! status: ${response.status}`;

      throw new Error(errorMessage);
    }

    return await response.json();
  } catch (error) {
    console.error('API request error:', error);
    throw error;
  }
};

function SearchableDropdown({
  label,
  value,
  onChange,
  options,
  placeholder,
  required = false,
  displayKey = 'name',
  valueKey = 'id',
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener(
      'mousedown',
      handleClickOutside
    );

    return () => {
      document.removeEventListener(
        'mousedown',
        handleClickOutside
      );
    };
  }, []);

  const filteredOptions = options.filter((option) =>
    option[displayKey]
      ?.toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const selectedOption = options.find(
    (opt) => opt[valueKey] === value
  );

  return (
    <div ref={dropdownRef} className="relative">
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label}{' '}
        {required && (
          <span className="text-red-500">*</span>
        )}
      </label>

      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setIsOpen(!isOpen)}
        className={`w-full px-4 py-2 text-left border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center justify-between ${
          disabled
            ? 'bg-gray-100 cursor-not-allowed'
            : 'bg-white'
        }`}
      >
        <span
          className={
            selectedOption
              ? 'text-gray-900'
              : 'text-gray-400'
          }
        >
          {selectedOption
            ? selectedOption[displayKey]
            : placeholder}
        </span>

        <svg
          className={`w-5 h-5 text-gray-400 transition-transform ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && !disabled && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg">
          <div className="p-2 border-b border-gray-200">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              placeholder="Search..."
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onClick={(e) => e.stopPropagation()}
            />
          </div>

          <div className="max-h-60 overflow-y-auto">
            {filteredOptions.length === 0 ? (
              <div className="px-4 py-3 text-sm text-gray-500 text-center">
                No options found
              </div>
            ) : (
              filteredOptions.map((option) => (
                <button
                  key={option[valueKey]}
                  type="button"
                  onClick={() => {
                    onChange(option[valueKey]);
                    setIsOpen(false);
                    setSearchTerm('');
                  }}
                  className={`w-full px-4 py-2 text-left text-sm hover:bg-blue-50 transition-colors ${
                    option[valueKey] === value
                      ? 'bg-blue-100 text-blue-700'
                      : 'text-gray-900'
                  }`}
                >
                  <div className="font-medium">
                    {option[displayKey]}
                  </div>

                  {option.subtitle && (
                    <div className="text-xs text-gray-500 mt-0.5">
                      {option.subtitle}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function CreateInvoiceModal({
  isOpen,
  onClose,
  onSuccess,
}) {
  const [isSubmitting, setIsSubmitting] =
    useState(false);

  const [error, setError] = useState(null);

  const [bookings, setBookings] = useState([]);

  const [customers, setCustomers] = useState([]);

  const [loadingData, setLoadingData] =
    useState(false);

  const [formData, setFormData] = useState({
    booking_id: '',
    customer_id: '',
    due_date: '',
    tax_rate: 18,
    items: [
      {
        description: '',
        quantity: 1,
        unit_price: 0,
      },
    ],
    notes: '',
    bank_name: '',
    bank_code: '',
    account_number: '',
    sort_code: '',
  });

  useEffect(() => {
    if (isOpen) {
      fetchBookingsAndCustomers();
    }
  }, [isOpen]);

  const fetchAllPaginatedBookings =
    async () => {
      const allBookings = [];

      let page = 1;

      const limit = 100;

      let totalPages = 1;

      while (page <= totalPages) {
        const response = await fetch(
          `${PAGINATED_BOOKINGS_URL}?page=${page}&limit=${limit}`,
          {
            headers: {
              'Content-Type':
                'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(
            `Failed to load bookings. Status: ${response.status}`
          );
        }

        const result = await response.json();

        const pageBookings = Array.isArray(
          result.data
        )
          ? result.data
          : [];

        allBookings.push(...pageBookings);

        const total =
          result.meta?.total ??
          result.meta?.total_count ??
          result.meta?.count ??
          result.total ??
          result.total_count;

        totalPages =
          result.meta?.totalPages ??
          result.meta?.total_pages ??
          result.totalPages ??
          result.total_pages ??
          (total
            ? Math.ceil(total / limit)
            : pageBookings.length < limit
            ? page
            : page + 1);

        if (pageBookings.length === 0) {
          break;
        }

        page += 1;
      }

      return allBookings;
    };

  const fetchBookingsAndCustomers =
    async () => {
      try {
        setLoadingData(true);
        setError(null);

        const [
          bookingsData,
          customersResponse,
          resourcesResponse,
        ] = await Promise.all([
          fetchAllPaginatedBookings(),
          fetch(
            `${CUSTOMERS_URL}/getAllCustomer`,
            {
              headers: {
                'Content-Type':
                  'application/json',
                Authorization: `Bearer ${getAccessToken()}`,
              },
            }
          ),
          fetch(RESOURCE_URL, {
            headers: {
              'Content-Type':
                'application/json',
              Authorization: `Bearer ${getAccessToken()}`,
            },
          }),
        ]);

        if (!customersResponse.ok) {
          throw new Error(
            'Failed to load customers'
          );
        }

        if (!resourcesResponse.ok) {
          throw new Error(
            'Failed to load resources'
          );
        }

        const customersData =
          await customersResponse.json();

        const resourcesData =
          await resourcesResponse.json();

        const rawCustomersList =
          Array.isArray(customersData)
            ? customersData
            : customersData.customers || [];

        const emailToCustomerIdMap = {};

        rawCustomersList.forEach(
          (customer) => {
            if (customer.email) {
              emailToCustomerIdMap[
                customer.email.toLowerCase()
              ] =
                customer._id ||
                customer.id;
            }
          }
        );

        const resourceMap = {};

        (
          resourcesData.data || []
        ).forEach((resource) => {
          resourceMap[resource.id] =
            resource.name;
        });

        const transformedBookings =
          bookingsData.map((booking) => {
            const resourceName =
              booking.resource?.id
                ? resourceMap[
                    booking.resource.id
                  ] || 'Resource'
                : 'Resource';

            let extractedCustomerId =
              booking.customer_id ||
              booking.metadata?.customer_id;

            if (
              extractedCustomerId &&
              typeof extractedCustomerId ===
                'string' &&
              extractedCustomerId.includes('@')
            ) {
              extractedCustomerId =
                emailToCustomerIdMap[
                  extractedCustomerId.toLowerCase()
                ] || extractedCustomerId;
            }

            return {
              id: booking.id,
              customer_id:
                extractedCustomerId,
              name: resourceName,
              subtitle: `${
                booking.metadata
                  ?.customer_name || 'N/A'
              } - ${new Date(
                booking.starts_at
              ).toLocaleDateString()}`,
            };
          });

        const transformedCustomers =
          rawCustomersList.map(
            (customer) => ({
              id:
                customer._id ||
                customer.id,
              name: customer.fullName,
              subtitle: customer.email,
            })
          );

        setBookings(transformedBookings);
        setCustomers(transformedCustomers);
      } catch (err) {
        console.error(
          'Error fetching data:',
          err
        );

        setError(
          err.message ||
            'Failed to load bookings and customers'
        );
      } finally {
        setLoadingData(false);
      }
    };

  if (!isOpen) return null;

  const updateFormData = (
    field,
    value
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCustomerChange = (
    customerId
  ) => {
    setFormData((prev) => ({
      ...prev,
      customer_id: customerId,
      booking_id: '',
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          description: '',
          quantity: 1,
          unit_price: 0,
        },
      ],
    }));
  };

  const updateItem = (
    index,
    field,
    value
  ) => {
    const newItems = [...formData.items];

    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };

    setFormData((prev) => ({
      ...prev,
      items: newItems,
    }));
  };

  const removeItem = (index) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter(
        (_, i) => i !== index
      ),
    }));
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      if (!formData.customer_id.trim()) {
        setError('Customer ID is required');
        return;
      }

      if (!formData.booking_id.trim()) {
        setError('Booking ID is required');
        return;
      }

      if (!formData.due_date) {
        setError('Due date is required');
        return;
      }

      const validItems =
        formData.items.filter(
          (item) =>
            item.description &&
            item.description.trim() !== ''
        );

      const requestBody = {
        booking_id:
          formData.booking_id,
        customer_id:
          formData.customer_id,
        due_date:
          formData.due_date,
        tax_rate:
          parseFloat(
            formData.tax_rate
          ) || 0,
        additional_items:
          validItems.map((item) => ({
            description:
              item.description.trim(),
            quantity:
              parseInt(
                item.quantity
              ) || 1,
            unit_price:
              parseFloat(
                item.unit_price
              ) || 0,
          })),
        notes: formData.notes,
        bank_name:
          formData.bank_name,
        bank_code:
          formData.bank_code,
        account_number:
          formData.account_number,
        sort_code:
          formData.sort_code,
      };

      const response =
        await apiRequest(
          '/createBookingInvoice',
          'POST',
          requestBody
        );

      console.log(
        'Invoice created:',
        response
      );

      const successMessage =
        document.createElement(
          'div'
        );

      successMessage.className =
        'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';

      successMessage.textContent =
        'Invoice created successfully!';

      document.body.appendChild(
        successMessage
      );

      setTimeout(() => {
        if (
          successMessage.parentNode
        ) {
          successMessage.parentNode.removeChild(
            successMessage
          );
        }
      }, 3000);

      setFormData({
        booking_id: '',
        customer_id: '',
        due_date: '',
        tax_rate: 18,
        items: [
          {
            description: '',
            quantity: 1,
            unit_price: 0,
          },
        ],
        notes: '',
        bank_name: '',
        bank_code: '',
        account_number: '',
        sort_code: '',
      });

      onClose();

      if (onSuccess) {
        await onSuccess();
      }
    } catch (err) {
      console.error(
        'Error creating invoice:',
        err
      );

      const errorMessage =
        err.message || '';

      if (
        errorMessage.includes(
          'E11000'
        ) ||
        errorMessage.includes(
          'duplicate key'
        ) ||
        errorMessage.includes(
          'invoice_number'
        )
      ) {
        const match =
          errorMessage.match(
            /invoice_number:\s*"([^"]+)"/
          );

        const invoiceNumber =
          match ? match[1] : '';

        setError(
          `An invoice already exists for this booking${
            invoiceNumber
              ? ` (${invoiceNumber})`
              : ''
          }. Please check existing invoices.`
        );
      } else {
        setError(
          errorMessage ||
            'Failed to create invoice'
        );
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const availableBookings =
    formData.customer_id
      ? bookings.filter(
          (booking) =>
            String(
              booking.customer_id
            ) ===
            String(
              formData.customer_id
            )
        )
      : [];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Create Booking Invoice
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              Generate a new invoice for a booking
            </p>
          </div>

          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {error && (
          <div className="mx-6 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />

            <div className="flex-1">
              <p className="text-sm font-medium text-red-800">
                Error
              </p>

              <p className="text-sm text-red-700 mt-1">
                {error}
              </p>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto px-6 py-6">
          <div className="space-y-6">
            {loadingData ? (
              <div className="text-center py-4">
                <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>

                <p className="mt-2 text-sm text-gray-500">
                  Loading bookings and customers...
                </p>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <SearchableDropdown
                    label="Customer"
                    value={
                      formData.customer_id
                    }
                    onChange={
                      handleCustomerChange
                    }
                    options={customers}
                    placeholder="Select a customer"
                    required={true}
                    displayKey="name"
                    valueKey="id"
                  />

                  <SearchableDropdown
                    label="Booking"
                    value={
                      formData.booking_id
                    }
                    onChange={(value) =>
                      updateFormData(
                        'booking_id',
                        value
                      )
                    }
                    options={
                      availableBookings
                    }
                    placeholder={
                      formData.customer_id
                        ? availableBookings.length >
                          0
                          ? 'Select a booking'
                          : 'No bookings found for this customer'
                        : 'Select a customer first'
                    }
                    disabled={
                      !formData.customer_id ||
                      availableBookings.length ===
                        0
                    }
                    required={true}
                    displayKey="name"
                    valueKey="id"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Due Date{' '}
                      <span className="text-red-500">
                        *
                      </span>
                    </label>

                    <input
                      type="date"
                      value={
                        formData.due_date
                      }
                      onChange={(e) =>
                        updateFormData(
                          'due_date',
                          e.target.value
                        )
                      }
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tax Rate (%)
                    </label>

                    <input
                      type="number"
                      value={
                        formData.tax_rate
                      }
                      onChange={(e) =>
                        updateFormData(
                          'tax_rate',
                          e.target.value
                        )
                      }
                      min="0"
                      max="100"
                      step="0.01"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <label className="block text-sm font-medium text-gray-700">
                      Invoice Items
                    </label>

                    <button
                      onClick={addItem}
                      className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                    >
                      <Plus className="w-4 h-4" />
                      Add Item
                    </button>
                  </div>

                  <div className="space-y-3">
                    {formData.items.map(
                      (item, index) => (
                        <div
                          key={index}
                          className="p-4 border border-gray-200 rounded-lg"
                        >
                          <div className="grid grid-cols-12 gap-3">
                            <div className="col-span-5">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Description
                              </label>

                              <input
                                type="text"
                                value={
                                  item.description
                                }
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    'description',
                                    e.target.value
                                  )
                                }
                                placeholder="e.g., Desk booking"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            <div className="col-span-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Quantity
                              </label>

                              <input
                                type="number"
                                value={
                                  item.quantity
                                }
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    'quantity',
                                    e.target.value
                                  )
                                }
                                min="1"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            <div className="col-span-3">
                              <label className="block text-xs font-medium text-gray-700 mb-1">
                                Unit Price
                              </label>

                              <input
                                type="number"
                                value={
                                  item.unit_price
                                }
                                onChange={(e) =>
                                  updateItem(
                                    index,
                                    'unit_price',
                                    e.target.value
                                  )
                                }
                                min="0"
                                step="0.01"
                                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                              />
                            </div>

                            <div className="col-span-1 flex items-end">
                              {formData.items
                                .length >
                                1 && (
                                <button
                                  onClick={() =>
                                    removeItem(
                                      index
                                    )
                                  }
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </div>

                          <div className="mt-2 text-sm text-gray-600">
                            Amount:{' '}
                            {(
                              item.quantity *
                              item.unit_price
                            ).toFixed(2)}
                          </div>
                        </div>
                      )
                    )}
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-gray-200">
                  <h3 className="text-sm font-medium text-gray-700 mb-3">
                    Bank Details (Optional)
                  </h3>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Bank Name
                      </label>

                      <input
                        type="text"
                        value={
                          formData.bank_name
                        }
                        onChange={(e) =>
                          updateFormData(
                            'bank_name',
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                        placeholder="e.g., BCA"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Account Number
                      </label>

                      <input
                        type="text"
                        value={
                          formData.account_number
                        }
                        onChange={(e) =>
                          updateFormData(
                            'account_number',
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Bank Code
                      </label>

                      <input
                        type="text"
                        value={
                          formData.bank_code
                        }
                        onChange={(e) =>
                          updateFormData(
                            'bank_code',
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Sort Code
                      </label>

                      <input
                        type="text"
                        value={
                          formData.sort_code
                        }
                        onChange={(e) =>
                          updateFormData(
                            'sort_code',
                            e.target.value
                          )
                        }
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notes
                  </label>

                  <textarea
                    value={formData.notes}
                    onChange={(e) =>
                      updateFormData(
                        'notes',
                        e.target.value
                      )
                    }
                    placeholder="Additional notes..."
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </>
            )}
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={
              isSubmitting ||
              !formData.booking_id ||
              !formData.customer_id
            }
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
          >
            {isSubmitting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                Creating...
              </>
            ) : (
              'Create Invoice'
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

function PDFPreviewModal({
  isOpen,
  onClose,
  pdfUrl,
  invoiceNumber,
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Invoice Preview
            </h2>

            <p className="text-sm text-gray-500 mt-1">
              {invoiceNumber}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <a
              href={pdfUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
            >
              <ExternalLink className="w-4 h-4" />
              Open in New Tab
            </a>

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title={`Invoice ${invoiceNumber}`}
          />
        </div>
      </div>
    </div>
  );
}

function DeleteConfirmModal({
  isOpen,
  onClose,
  onConfirm,
  invoiceNumber,
}) {
  const [isDeleting, setIsDeleting] =
    useState(false);

  if (!isOpen) return null;

  const handleConfirm = async () => {
    setIsDeleting(true);
    await onConfirm();
    setIsDeleting(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        <div className="p-6">
          <div className="flex items-center mb-4">
            <div className="flex-shrink-0 w-10 h-10 mx-auto bg-red-100 rounded-full flex items-center justify-center">
              <svg
                className="w-6 h-6 text-red-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
                />
              </svg>
            </div>
          </div>

          <div className="text-center">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Invoice
            </h3>

            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete
              invoice "{invoiceNumber}"? This
              action cannot be undone.
            </p>

            <div className="flex gap-3 justify-center">
              <button
                onClick={onClose}
                disabled={isDeleting}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              <button
                onClick={handleConfirm}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isDeleting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Invoice'
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function BookingInvoices() {
  const [invoices, setInvoices] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState(null);

  const [showCreateModal, setShowCreateModal] =
    useState(false);

  const [showDeleteModal, setShowDeleteModal] =
    useState(false);

  const [showPreviewModal, setShowPreviewModal] =
    useState(false);

  const [selectedInvoice, setSelectedInvoice] =
    useState(null);

  const [searchTerm, setSearchTerm] =
    useState('');

  const [sendingEmailId, setSendingEmailId] =
    useState(null);

  useEffect(() => {
    fetchInvoices();
  }, []);

  const fetchInvoices = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(
        '/getAllBookingInvoice',
        'GET'
      );

      setInvoices(response.data || []);
    } catch (err) {
      setError(err.message);

      console.error(
        'Failed to fetch invoices:',
        err
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (
    invoice
  ) => {
    setSelectedInvoice(invoice);
    setShowDeleteModal(true);
  };

  const handlePreviewClick = (
    invoice
  ) => {
    setSelectedInvoice(invoice);
    setShowPreviewModal(true);
  };

  const handleDeleteConfirm =
    async () => {
      try {
        await apiRequest(
          '/deleteBookingInvoice',
          'DELETE',
          {
            invoice_id:
              selectedInvoice._id,
          }
        );

        await fetchInvoices();

        setShowDeleteModal(false);
        setSelectedInvoice(null);

        const successMessage =
          document.createElement(
            'div'
          );

        successMessage.className =
          'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';

        successMessage.textContent =
          'Invoice deleted successfully!';

        document.body.appendChild(
          successMessage
        );

        setTimeout(() => {
          if (
            successMessage.parentNode
          ) {
            successMessage.parentNode.removeChild(
              successMessage
            );
          }
        }, 3000);
      } catch (err) {
        console.error(
          'Delete error:',
          err
        );

        setError(err.message);
      }
    };

  const handleSendEmail = async (
    invoice
  ) => {
    console.log(
      'Sending email for invoice:',
      invoice
    );

    if (
      !invoice.customer_email ||
      !invoice.pdf_path
    ) {
      alert(
        'Cannot send email: Missing customer email or PDF link.'
      );

      return;
    }

    try {
      setSendingEmailId(invoice._id);

      const response = await fetch(
        `${BOOKING_INVOICE_URL}/sendBookingInvoice`,
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          body: JSON.stringify({
            email:
              invoice.customer_email,
            pdf_url:
              invoice.pdf_path,
          }),
        }
      );

      if (!response.ok) {
        throw new Error(
          'Failed to send email'
        );
      }

      const successMessage =
        document.createElement(
          'div'
        );

      successMessage.className =
        'fixed top-4 right-4 bg-[#79C485] text-white px-6 py-3 rounded-lg shadow-lg z-50';

      successMessage.textContent = `Invoice sent to ${invoice.customer_email}`;

      document.body.appendChild(
        successMessage
      );

      setTimeout(() => {
        if (
          successMessage.parentNode
        ) {
          successMessage.parentNode.removeChild(
            successMessage
          );
        }
      }, 3000);
    } catch (err) {
      console.error(
        'Email error:',
        err
      );

      alert(
        'Failed to send invoice email. Please try again.'
      );
    } finally {
      setSendingEmailId(null);
    }
  };

  const formatDate = (
    dateString
  ) => {
    if (!dateString) return 'N/A';

    return new Date(
      dateString
    ).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (
    amount
  ) => {
    return new Intl.NumberFormat(
      'en-US',
      {
        style: 'currency',
        currency: 'IDR',
      }
    ).format(amount || 0);
  };

  const filteredInvoices =
    invoices.filter(
      (invoice) =>
        invoice.invoice_number
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          ) ||
        invoice.customer_name
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          ) ||
        invoice.customer_email
          ?.toLowerCase()
          .includes(
            searchTerm.toLowerCase()
          )
    );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />

            <input
              type="text"
              placeholder="Search invoices..."
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(
                  e.target.value
                )
              }
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>

        <button
          onClick={() =>
            setShowCreateModal(true)
          }
          className="flex items-center gap-2 px-4 py-2 bg-[#1A5090] text-white rounded-lg hover:bg-blue-900"
        >
          <Plus className="w-4 h-4" />

          <span className="text-sm font-medium">
            Create Invoice
          </span>
        </button>
      </div>

      {loading && (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>

          <p className="mt-2 text-gray-500">
            Loading invoices...
          </p>
        </div>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          <p className="font-medium">
            Error loading invoices
          </p>

          <p className="text-sm">
            {error}
          </p>
        </div>
      )}

      {!loading &&
        !error &&
        filteredInvoices.length ===
          0 && (
          <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
            <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />

            <p className="text-gray-500">
              {searchTerm
                ? 'No invoices found matching your search'
                : 'No invoices found'}
            </p>

            {!searchTerm && (
              <button
                onClick={() =>
                  setShowCreateModal(
                    true
                  )
                }
                className="mt-4 px-4 py-2 bg-[#1A5090] text-white rounded-lg hover:bg-blue-900"
              >
                <Plus className="w-4 h-4 inline mr-2" />
                Create your first
                invoice
              </button>
            )}
          </div>
        )}

      {!loading &&
        !error &&
        filteredInvoices.length >
          0 && (
          <div className="space-y-4">
            {filteredInvoices.map(
              (invoice) => (
                <div
                  key={invoice._id}
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 grid grid-cols-4 gap-6">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <FileText className="w-4 h-4 text-gray-400" />

                          <span className="text-xs text-gray-500">
                            Invoice Number
                          </span>
                        </div>

                        <p className="font-semibold text-sm text-gray-900">
                          {
                            invoice.invoice_number
                          }
                        </p>

                        <span
                          className={`inline-block mt-1 px-2 py-1 text-xs rounded-full ${
                            invoice.status ===
                            'paid'
                              ? 'bg-green-100 text-green-800'
                              : invoice.status ===
                                'draft'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {invoice.status ||
                            'draft'}
                        </span>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <User className="w-4 h-4 text-gray-400" />

                          <span className="text-xs text-gray-500">
                            Customer
                          </span>
                        </div>

                        <p className="font-medium text-gray-900">
                          {
                            invoice.customer_name
                          }
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <Calendar className="w-4 h-4 text-gray-400" />

                          <span className="text-xs text-gray-500">
                            Dates
                          </span>
                        </div>

                        <p className="text-sm text-gray-900">
                          <span className="font-medium">
                            Issue:
                          </span>{' '}
                          {formatDate(
                            invoice.issue_date
                          )}
                        </p>

                        <p className="text-sm text-gray-900">
                          <span className="font-medium">
                            Due:
                          </span>{' '}
                          {formatDate(
                            invoice.due_date
                          )}
                        </p>
                      </div>

                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <p className="text-xs text-gray-600">
                            IDR
                          </p>

                          <span className="text-xs text-gray-500">
                            Amount
                          </span>
                        </div>

                        <p className="text-sm text-gray-600">
                          Subtotal:{' '}
                          {formatCurrency(
                            invoice.subtotal
                          )}
                        </p>

                        <p className="text-sm text-gray-600">
                          Tax (
                          {
                            invoice.tax_rate
                          }
                          %):{' '}
                          {formatCurrency(
                            invoice.tax_amount
                          )}
                        </p>

                        <p className="text-lg font-bold text-gray-900 mt-1">
                          {formatCurrency(
                            invoice.total_amount
                          )}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 ml-6">
                      {invoice.pdf_path && (
                        <>
                          <button
                            onClick={() =>
                              handleSendEmail(
                                invoice
                              )
                            }
                            disabled={
                              sendingEmailId ===
                              invoice._id
                            }
                            className="p-1.5 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50"
                            title="Send via Email"
                          >
                            {sendingEmailId ===
                            invoice._id ? (
                              <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                            ) : (
                              <Mail className="w-4 h-4" />
                            )}
                          </button>

                          <button
                            onClick={() =>
                              handlePreviewClick(
                                invoice
                              )
                            }
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Preview PDF"
                          >
                            <Eye className="w-5 h-5" />
                          </button>

                          <a
                            href={
                              invoice.pdf_path
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download PDF"
                          >
                            <Download className="w-5 h-5" />
                          </a>
                        </>
                      )}

                      <button
                        onClick={() =>
                          handleDeleteClick(
                            invoice
                          )
                        }
                        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete invoice"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>

                  {invoice.items &&
                    invoice.items.length >
                      0 && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <p className="text-xs text-gray-500 mb-2">
                          Items:
                        </p>

                        <div className="space-y-1">
                          {invoice.items.map(
                            (
                              item,
                              index
                            ) => (
                              <div
                                key={index}
                                className="flex justify-between text-sm"
                              >
                                <span className="text-gray-700">
                                  {
                                    item.description
                                  }{' '}
                                  (x
                                  {
                                    item.quantity
                                  }
                                  )
                                </span>

                                <span className="text-gray-900 font-medium">
                                  {formatCurrency(
                                    item.amount
                                  )}
                                </span>
                              </div>
                            )
                          )}
                        </div>
                      </div>
                    )}

                  {invoice.notes && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <p className="text-xs text-gray-500 mb-1">
                        Notes:
                      </p>

                      <p className="text-sm text-gray-700">
                        {invoice.notes}
                      </p>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
        )}

      <CreateInvoiceModal
        isOpen={showCreateModal}
        onClose={() =>
          setShowCreateModal(false)
        }
        onSuccess={fetchInvoices}
      />

      <PDFPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setSelectedInvoice(null);
        }}
        pdfUrl={
          selectedInvoice?.pdf_path
        }
        invoiceNumber={
          selectedInvoice?.invoice_number
        }
      />

      <DeleteConfirmModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedInvoice(null);
        }}
        onConfirm={handleDeleteConfirm}
        invoiceNumber={
          selectedInvoice?.invoice_number
        }
      />
    </div>
  );
}